import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { db, doc, onSnapshot, fmt, RAZORPAY_KEY } from "../config/firebase";
import PhoneNumberModal from "./PhoneNumberModal";
import "./CartSidebar.css";

const FUNCTIONS_BASE = "https://us-central1-sunrise-photostat.cloudfunctions.net";

const toMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

// Loads the Razorpay checkout script only when the user actually checks out,
// instead of on every page load. Caches the promise so repeated clicks don't
// re-inject the script.
let razorpayScriptPromise = null;
const loadRazorpayScript = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => {
      razorpayScriptPromise = null; // allow retry on next click if it failed
      reject(new Error("Failed to load Razorpay SDK"));
    };
    document.body.appendChild(script);
  });

  return razorpayScriptPromise;
};

const CartSidebar = ({ isOpen, onClose, onShowToast }) => {
  const { cart, updateCartQty, removeFromCart, getCartTotal, clearCart } = useCart();
  const { user, userProfile, setUserProfile } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [paymentMode, setPaymentMode] = useState("advance"); // "advance" | "full"
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [storeHours, setStoreHours] = useState({
    enabled: false,
    openTime: "09:00",
    closeTime: "20:30",
    forceClosed: false,
    closureReason: ""
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "settings", "storeHours"),
      (snap) => {
        if (snap.exists()) {
          setStoreHours((prev) => ({ ...prev, ...snap.data() }));
        }
      },
      (err) => {
        console.error("Error fetching store hours:", err);
      }
    );
    return () => unsubscribe();
  }, []);

  const isStoreOpen = () => {
    if (storeHours.forceClosed) return false; // manual override always wins
    if (!storeHours.enabled) return true;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const openMinutes = toMinutes(storeHours.openTime);
    const closeMinutes = toMinutes(storeHours.closeTime);
    if (openMinutes == null || closeMinutes == null) return true;
    return nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
  };

  const storeOpen = isStoreOpen();

  const closedMessage = storeHours.forceClosed
    ? (storeHours.closureReason || "We're temporarily closed. Please check back later.")
    : `We're closed right now. Orders accepted ${formatTime(storeHours.openTime)} - ${formatTime(storeHours.closeTime)}.`;

  const subtotal = getCartTotal();
  const advanceAmount = Math.round(subtotal * 0.3);
  const balanceAmount = subtotal - advanceAmount;
  const fullAmount = subtotal;
  const grandTotal = subtotal;

  const handleCheckout = async () => {
    if (!storeOpen) {
      onShowToast(closedMessage, "error");
      return;
    }

    if (!user) {
      onShowToast("Please login to checkout", "info");
      onClose();
      navigate("/login");
      return;
    }

    if (cart.length === 0) {
      onShowToast("Your cart is empty", "error");
      return;
    }

    // Phone number is required to place an order (e.g. Google sign-in users
    // don't get asked for one at signup). Ask for it here before payment.
    if (!userProfile?.phone) {
      setShowPhoneModal(true);
      return;
    }

    await proceedToPayment();
  };

  const handlePhoneSaved = async (newPhone) => {
    setUserProfile((prev) => ({ ...(prev || {}), phone: newPhone }));
    setShowPhoneModal(false);
    await proceedToPayment();
  };

  const proceedToPayment = async () => {
    setProcessing(true);

    const isAdvance = paymentMode === "advance";
    const payAmount = isAdvance ? advanceAmount : fullAmount;
    const orderTotal = grandTotal;

    try {
      // Load Razorpay SDK now (only happens on first checkout click)
      await loadRazorpayScript();

      // Get Firebase auth token to authenticate with Cloud Functions
      const idToken = await user.getIdToken();

      // 1. Create the Razorpay order server-side (never trust a client-made order_id)
      const orderRes = await fetch(`${FUNCTIONS_BASE}/createRazorpayOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          amount: payAmount * 100,
          currency: "INR",
          receipt: `receipt_${Date.now()}`
        })
      });

      if (!orderRes.ok) {
        throw new Error("Failed to create order");
      }

      const razorpayOrder = await orderRes.json();

      const options = {
        key: RAZORPAY_KEY,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Sunrise Stationery",
        description: isAdvance ? `30% Advance (₹${payAmount})` : `Full Payment (₹${payAmount})`,
        image: "https://picsum.photos/100/100",
        order_id: razorpayOrder.orderId,

        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
        },

        handler: async function (response) {
          try {
            // 2. Verify payment signature server-side BEFORE saving the order
            const verifyRes = await fetch(`${FUNCTIONS_BASE}/verifyPayment`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                orderData: {
                  items: cart.map((item) => ({
                    productId: item.productId,
                    productName: item.productName,
                    variantLabel: item.variantLabel,
                    price: item.price,
                    quantity: item.quantity
                  })),
                  subtotal: subtotal,
                  advance: isAdvance ? advanceAmount : fullAmount,
                  total: orderTotal,
                  paymentMode: isAdvance ? "advance" : "full",
                  balanceDue: isAdvance ? balanceAmount : 0,
                  customerName: userProfile?.name || user.displayName || "",
                  customerPhone: userProfile?.phone || "",
                  notes: isAdvance ? `Balance ₹${balanceAmount} to be paid on pickup` : "Fully paid"
                }
              })
            });

            if (!verifyRes.ok) {
              const errData = await verifyRes.json().catch(() => ({}));
              throw new Error(errData.error || "Verification failed");
            }

            clearCart();
            onClose();
            onShowToast(
              isAdvance
                ? `Order placed! Pay ₹${fmt(balanceAmount)} on pickup.`
                : "Order placed! Fully paid.",
              "success"
            );
            navigate("/orders");
          } catch (err) {
            console.error("Error verifying/saving order:", err);
            onShowToast("Payment verification failed. Contact support with your payment ID.", "error");
          } finally {
            setProcessing(false);
          }
        },
        prefill: {
          name: userProfile?.name || user.displayName || "",
          email: user.email || "",
          contact: userProfile?.phone || ""
        },
        theme: {
          color: "#FF6B35"
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            onShowToast("Payment cancelled", "info");
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Checkout error:", err);
      onShowToast("Something went wrong. Please try again.", "error");
      setProcessing(false);
    }
  };

  return (
    <>
      <div className={`cart-overlay ${isOpen ? "open" : ""}`} onClick={onClose}></div>
      <div className={`cart-sidebar ${isOpen ? "open" : ""}`}>
        <div className="cart-header">
          <h2>Your Cart</h2>
          <button className="cart-close" onClick={onClose}>×</button>
        </div>

        {cart.length === 0 ? (
          <div className="cart-empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            <p>Your cart is empty</p>
            <button className="btn-shop" onClick={onClose}>Start Shopping</button>
          </div>
        ) : (
          <>
            <div className="cart-items">
              {!storeOpen && (
                <div className="store-closed-banner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                  <span>{closedMessage}</span>
                </div>
              )}
              {cart.map((item, idx) => (
                <div key={`${item.productId}-${item.variantLabel || "default"}-${idx}`} className="cart-item">
                  <img
                    src={item.image || "https://picsum.photos/100/100"}
                    alt={item.productName}
                    className="cart-item-img"
                  />
                  <div className="cart-item-details">
                    <h4 className="cart-item-name">{item.productName}</h4>
                    {item.variantLabel && (
                      <span className="cart-item-variant">{item.variantLabel}</span>
                    )}
                    <span className="cart-item-price">{fmt(item.price)}</span>
                  </div>
                  <div className="cart-item-actions">
                    <div className="qty-control">
                      <button onClick={() => updateCartQty(item.productId, item.variantLabel, item.quantity - 1)}>−</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateCartQty(item.productId, item.variantLabel, item.quantity + 1)}>+</button>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => removeFromCart(item.productId, item.variantLabel)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-footer">
              <div className="cart-summary">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{fmt(subtotal)}</span>
                </div>
              </div>

              <div className="payment-mode-section">
                <label className="payment-mode-label">Choose Payment Option</label>
                <div className="payment-options">
                  <button
                    className={`payment-option ${paymentMode === "advance" ? "active" : ""}`}
                    onClick={() => setPaymentMode("advance")}
                  >
                    <div className="option-header">
                      <span className="option-radio"></span>
                      <span className="option-title">30% Advance</span>
                      <span className="option-amount">{fmt(advanceAmount)}</span>
                    </div>
                    <div className="option-detail">
                      Pay {fmt(advanceAmount)} now. Balance {fmt(balanceAmount)} on pickup.
                    </div>
                  </button>

                  <button
                    className={`payment-option ${paymentMode === "full" ? "active" : ""}`}
                    onClick={() => setPaymentMode("full")}
                  >
                    <div className="option-header">
                      <span className="option-radio"></span>
                      <span className="option-title">Full Payment</span>
                      <span className="option-amount">{fmt(fullAmount)}</span>
                    </div>
                    <div className="option-detail">
                      Pay {fmt(fullAmount)} now. Nothing due on pickup.
                    </div>
                  </button>
                </div>
              </div>

              <div className="cart-summary-final">
                <div className="summary-row total">
                  <span>Total</span>
                  <span>{fmt(grandTotal)}</span>
                </div>
                {paymentMode === "advance" && (
                  <div className="summary-row balance">
                    <span>Paying Now</span>
                    <span>{fmt(advanceAmount)}</span>
                  </div>
                )}
              </div>

              <button
                className="checkout-btn"
                onClick={handleCheckout}
                disabled={processing || !storeOpen}
              >
                {processing ? (
                  <>
                    <div className="btn-spinner"></div>
                    Processing...
                  </>
                ) : !storeOpen ? (
                  "Store Closed"
                ) : (
                  <>
                    {paymentMode === "advance"
                      ? `Pay ${fmt(advanceAmount)} Advance`
                      : `Pay ${fmt(fullAmount)} Full Amount`}
                  </>
                )}
              </button>

              <button className="clear-cart-btn" onClick={clearCart}>
                Clear Cart
              </button>
            </div>
          </>
        )}
      </div>

      {showPhoneModal && (
        <PhoneNumberModal
          user={user}
          onSuccess={handlePhoneSaved}
          onClose={() => setShowPhoneModal(false)}
        />
      )}
    </>
  );
};

export default CartSidebar;